/**
 * Checks if there are any worker messages present for any of the operations.
 *
 * @param {Array} operations - The list of operations to check.
 * @returns {boolean} True if there are any worker messages present, false otherwise.
 */
export const isWorkerMessagesPresent = (operations) => {
    if (!operations || operations.length === 0) {
        return false;
    }
    return operations.some(o => o.workerMessages && o.workerMessages.length > 0);
};

/**
 * Returns a list of operations that match the given operation type.
 *
 * @param {Array} operations - The list of operations to filter.
 * @param {string} operationType - The type of operation to filter by.
 * @returns {Array} A list of operations that match the given operation type.
 */
export const getOperationsByType = (operations, operationType) => {
    if (!operations || operations.length === 0) {
        return [];
    }
    return operations.filter(o => o.jobType === operationType);
};

/**
 * Returns the last message sent from the worker for the given operation.
 *
 * If the operation has no worker messages, an empty object is returned.
 *
 * @param {object} operation - The operation to get the last worker message for.
 * @param {Array} workerMessages - The list of worker messages for the given operation.
 * @returns {object} The last worker message sent from the worker, or an empty object if no messages are present.
 */
export const getOperationLastUpdate = (operation, workerMessages) => {
    if (!workerMessages || workerMessages.length === 0) {
        return {};
    }

    const lastMessage = workerMessages[workerMessages.length - 1];
    if (!lastMessage || !(lastMessage.details || lastMessage.timedOut || lastMessage.status === 'error')) {
        return {};
    }
    return lastMessage;
};

/**
 * Returns a human-readable title for the given stage ID.
 *
 * @param {function} t Translation function.
 * @param {string} stageId - The ID of the stage to get the human-readable title for.
 * @returns {string} A human-readable title for the given stage ID, or the original stage ID if no matching title is found.
 */
export const getStageHrTitle = (t, stageId) => {

    if (typeof (t) !== 'function') {
        throw new Error('t must be a function');
    }

    switch (stageId) {
        case 'savingdamsobjects':
            return t('stageTitleSavingDAMSObjects', 'oppretter DAMS objekter');
        case 'creatingdamsobjects':
            return t('stageTitleCreatingDAMSObjects', 'oppretter DAMS objekter');
        case 'filesuploaded':
            return t('stageTitleFilesUploaded', 'lastet opp');
        case 'creatingdamsdocuments':
            return t('stageTitleCreatingDAMSDocuments', 'oppretter DAMS objekter');
        case 'gettingobjectstatus':
            return '';
        case 'operationcomplete':
            return '';
        case 'extractingmetadata':
        case 'addedmetadata':
            return t('stageTitleFetchingMetadata', 'henter metadata');
        default:
            return stageId;
    }
};

export const getStatusHr = (t, status) => {
    if (typeof (t) !== 'function') {
        throw new Error('t must be a function');
    }

    switch (status) {
        case 'converting':
            return t('operationStateConverting', 'konverterer');
        case 'init':
            return t('operationStateInitializing', 'initialiserer');
        case 'done':
            return t('operationStateDone', 'ferdig');
        case 'error':
            return t('operationStateError', 'feil');
        default:
            return '';
    }
};

/**
 * Returns the progress of a file upload operation, given the list of files and the last update message.
 *
 * If the last update message does not contain the key `details`, the progress is considered to be 0.
 *
 * The progress is calculated as the number of processed files divided by the total number of files in the operation, multiplied by 100.
 *
 * @param {Array} files - The list of files in the operation.
 * @param {object} lastUpdate - The last update message sent from the worker.
 * @returns {number} The progress of the file upload operation, as a percentage.
 */
export const getProgress = (files, lastUpdate) => {
    if (!files || files.length === 0) {
        return 0;
    }
    if (!Object.keys(lastUpdate).includes('details')) {
        return 0;
    }
    let processed;
    if (Object.keys(lastUpdate.details).includes('failed') && Object.keys(lastUpdate.details).includes('success')) {
        processed = lastUpdate.details.failed.length + lastUpdate.details.success.length;
    } else {
        const {statuses = []} = lastUpdate.details;
        processed = statuses.filter(s => (s.status === 'done' || s.status === 'error' || s.status === 'failed'))?.length || 0;
    }
    return (processed / files.length) * 100;
};

const getStatusWithoutFiles = (t, timedOut, lastUpdate) => {
    if (timedOut) {
        return [t('operationStateTimedOut', 'tidsavbrudd')];
    }
    return lastUpdate?.status === 'error' ? [t('operationStateFailed', 'Opplastingen feilet')] : [];
};

/**
 * Returns a human-readable representation of the status of a file upload operation.
 *
 * If the operation has timed out, the status message is the translated 'tidsavbrudd'.
 * Otherwise, the status message is the human-readable representation of the status of the operation.
 * Without files, a timed-out operation gets the single translated timeout text and a failed one the single
 * translated failure text (timeout wins), so the row is never blank.
 *
 * Returns an array of strings, containing
 * - the number of files in the operation
 * - the human-readable representation of the status of the operation
 * - the human-readable title of the stage of the operation
 *
 * @param t
 * @param {boolean} timedOut - Whether the operation has timed out.
 * @param {object} lastUpdate - The last update message sent from the worker.
 * @param {Array} files - The list of files in the operation.
 * @returns {Array} The human-readable representation of the status of the file upload operation.
 */
export const getHrStatusDetails = (t, timedOut, lastUpdate, files) => {
    if (typeof (t) !== 'function') {
        throw new Error('t must be a function');
    }
    if (!files || files?.length === 0) {
        return getStatusWithoutFiles(t, timedOut, lastUpdate);
    }
    const statusMsg = timedOut ? t('operationStateTimedOut', 'tidsavbrudd') : getStatusHr(t, lastUpdate.status)
    const hrStatus = `${files.length} ${t('operationsLabelFiles', 'filer')}`;
    const hrStatusTitle = getStageHrTitle(t, lastUpdate.id);
    const hrStatusMsg = statusMsg;

    const hrStatusDetails = [];
    hrStatusDetails.push(hrStatus);
    if (hrStatusTitle) {
        hrStatusDetails.push(hrStatusTitle)
    }
    if (hrStatusMsg) {
        hrStatusDetails.push(hrStatusMsg);
    }
    return hrStatusDetails;
};

/**
 * Returns true if the operation is done, false otherwise.
 *
 * An operation is considered done if the status of the last message in the workerMessages array is 'done'
 * and the id of the message is either 'gettingobjectstatus' or 'operationcomplete' (if extractMetadata is false), or
 * 'extractingmetadata' (if extractMetadata is true).
 *
 * @param {object} o - The operation to check.
 * @param {boolean} done - The current done status. If not specified, it is set to false.
 * @returns {boolean} true if the operation is done, false otherwise.
 */
export const isOperationDone = (o, done = false) => {
    if (o.workerMessages) {
        const lastMessage = o.workerMessages.at(-1);
        if (!lastMessage) {
            return false;
        }
        const {status, id} = lastMessage;
        done = done
            || (status === 'done' && id === 'gettingobjectstatus')
            || (status === 'done' && id === 'operationcomplete');
    }
    return done;
};